import React from 'react'
import { Alert, Heading, Text, Stack, Box, Switch, Card, ImageLogo } from '@/components/lib'
import api from '../../../api'
import useStore from '../../../store'
import utils from '../../../utils/utils'

import FitbitImage from '../../../images/fitbit.png'
import GarminImage from '../../../images/garmin.png'
import DexcomImage from '../../../images/dexcom.png'

export default function IntegrationsSettings ({ user, trial }) {
  const trialId = trial?._id
  const { updateTrial, team } = useStore()
  const canManage = utils.canManageTrial(user, trial)

  function toggleFitbitIntegration (e) {
    api.trials.update(trialId, { fitbitEnabled: e.checked }, newTrial => {
      updateTrial(newTrial._id, { fitbitEnabled: newTrial.fitbitEnabled ?? false })
    }, err => {
      utils.toast('error', 'Error updating Fitbit integration', err.message)
    })
  }

  function toggleGarminIntegration (e) {
    api.trials.update(trialId, { garminEnabled: e.checked }, newTrial => {
      updateTrial(newTrial._id, { garminEnabled: newTrial.garminEnabled ?? false })
    }, err => {
      utils.toast('error', 'Error updating Garmin integration', err.message)
    })
  }

  function toggleDexcomIntegration (e) {
    api.trials.update(trialId, { dexcomEnabled: e.checked }, newTrial => {
      updateTrial(newTrial._id, { dexcomEnabled: newTrial.dexcomEnabled ?? false })
    }, err => {
      utils.toast('error', 'Error updating Dexcom integration', err.message)
    })
  }

  return (
    <Stack gap={5}>
      <Box>
        <Heading size='md'>Wearables &amp; integrations</Heading>
        <Text>Configure integrations for this trial.</Text>
      </Box>

      {utils.hasFeature(team, 'fitbit') &&
        <Card variant='outline' gap={2}>
          <Box>
            <ImageLogo p={2} display='inline-block' src={FitbitImage} alt='Fitbit' w={100} />
          </Box>
          <Stack gap={2}>
            <Heading size='sm'>Fitbit</Heading>
            <Text>Allow Trialflare to connect to the Fitbit accounts of trial participants.</Text>
            {utils.hasFeature(team, 'fitbit')
              ? (
                <Stack alignItems='center' direction='row' spacing={4}>
                  <Switch
                    id='fitbit'
                    checked={trial?.fitbitEnabled}
                    disabled={!canManage}
                    onChange={toggleFitbitIntegration}
                  />
                  <Text>Enable Fitbit integration</Text>
                </Stack>)
              : (
                <Alert status='info'>
                  The Fitbit integration is not enabled for this team. Contact your account manager for more information.
                </Alert>)}
          </Stack>
        </Card>}

      <Card variant='outline' gap={2}>
        <Box>
          <ImageLogo p={2} display='inline-block' src={GarminImage} alt='Garmin' w={100} />
        </Box>
        <Stack gap={2}>
          <Heading size='sm'>Garmin</Heading>
          <Text>Allow Trialflare to connect to the Garmin accounts of trial participants.</Text>
          {utils.hasFeature(team, 'garmin')
            ? (
              <Stack alignItems='center' direction='row' spacing={4}>
                <Switch
                  id='garmin'
                  checked={trial?.garminEnabled}
                  disabled={!canManage}
                  onChange={toggleGarminIntegration}
                />
                <Text>Enable Garmin integration</Text>
              </Stack>)
            : (
              <Alert status='info'>
                The Garmin integration is not enabled for this team. Contact your account manager for more information.
              </Alert>)}
        </Stack>
      </Card>

      {utils.hasFeature(team, 'dexcom') && (
        <Card variant='outline' gap={2}>
          <Box>
            <img src={DexcomImage} alt='Dexcom' width={100} />
          </Box>
          <Stack gap={2}>
            <Heading size='sm'>Dexcom</Heading>
            <Text>Allow Trialflare to connect to the Dexcom accounts of trial participants.</Text>
            {utils.hasFeature(team, 'dexcom')
              ? (
                <Stack alignItems='center' direction='row' spacing={4}>
                  <Switch
                    id='dexcom'
                    checked={trial?.dexcomEnabled}
                    disabled={!canManage}
                    onChange={toggleDexcomIntegration}
                  />
                  <Text>Enable Dexcom integration</Text>
                </Stack>)
              : (
                <Alert status='info'>
                  The Dexcom integration is not enabled for this team. Contact your account manager for more information.
                </Alert>)}
          </Stack>
        </Card>)}
    </Stack>
  )
}
